import React, { useEffect, useRef, useState } from 'react';

const keyframes = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 900px) {
    .cine-header { padding: 0 30px 40px !important; }
    .cine-clip { margin-bottom: 48px !important; }
  }
  @media (max-width: 560px) {
    .cine-header { padding: 0 20px 32px !important; }
    .cine-clip { margin-bottom: 36px !important; }
  }
  /* Pause/play control: visible on hover, and always visible on touch devices. */
  .cine-toggle { opacity: 0; transition: opacity 0.25s ease; }
  .cine-clip:hover .cine-toggle,
  .cine-clip:focus-within .cine-toggle { opacity: 1; }
  /* Until a clip has played once, keep the control visible: if a browser
     blocks autoplay the viewer sees a clear play button, never a bare frame. */
  .cine-toggle-always { opacity: 1; }
  @media (hover: none) { .cine-toggle { opacity: 1; } }
`;

// Cinematic clips - short, silent, atmospheric. Drop the exported MP4s into
// /public/videos/cinematics/ and add an entry for each, in display order:
//
//   { src: '/videos/cinematics/clip-01.mp4' },
//
// Add `ratio: '9 / 16'` to an entry for vertical footage. Every clip plays
// muted so it can autoplay in any browser.
const clips = [
  { src: '/videos/cinematics/clip-01.mp4' },
  { src: '/videos/cinematics/clip-03.mp4' },
  { src: '/videos/cinematics/clip-04.mp4' },
  { src: '/videos/cinematics/clip-06.mp4' },
  { src: '/videos/cinematics/clip-02.mp4' },
  { src: '/videos/cinematics/clip-05.mp4' },
];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0a',
    paddingTop: '120px',
    paddingBottom: '100px',
  },
  header: {
    padding: '0 60px 60px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  label: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.7rem',
    letterSpacing: '0.35em',
    textTransform: 'uppercase',
    color: '#c9a96e',
    marginBottom: '16px',
    display: 'block',
  },
  heading: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(3rem, 6vw, 6rem)',
    fontWeight: 300,
    lineHeight: 1,
    color: '#f5f2ee',
  },
  clip: {
    padding: '0 60px',
    maxWidth: '1400px',
    margin: '0 auto 56px',
    opacity: 0,
    animation: 'fadeUp 0.8s ease forwards',
  },
  frame: {
    position: 'relative',
    aspectRatio: '16 / 9',
    background: '#0f0d0a',
    border: '1px solid rgba(201,169,110,0.15)',
    overflow: 'hidden',
  },
  video: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  toggle: {
    position: 'absolute',
    right: '18px',
    bottom: '18px',
    width: '46px',
    height: '46px',
    borderRadius: '50%',
    background: 'rgba(10,10,10,0.6)',
    backdropFilter: 'blur(6px)',
    color: '#c9a96e',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.25s ease, border-color 0.25s ease',
  },
  missing: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '30px',
    textAlign: 'center',
  },
  missingText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: 'italic',
    fontSize: '1.05rem',
    color: '#3a3530',
    letterSpacing: '0.05em',
    margin: 0,
  },
  missingPath: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.7rem',
    color: '#4a443e',
    letterSpacing: '0.06em',
  },
  empty: {
    margin: '0 auto',
    maxWidth: '1280px',
    border: '1px solid rgba(201,169,110,0.12)',
    padding: '70px 40px',
    textAlign: 'center',
    background: 'linear-gradient(160deg, #1a1510, #0f0d0a)',
  },
  emptyText: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: 'italic',
    fontSize: '1.2rem',
    color: '#3a3530',
    letterSpacing: '0.05em',
    margin: '0 0 10px',
  },
  emptyHint: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: '0.75rem',
    color: '#4a443e',
    letterSpacing: '0.08em',
    margin: 0,
  },
};

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Reports whether the element is showing, so only visible clips play.
// threshold 0 with a negative bottom rootMargin: a clip counts as visible once
// it has entered the upper ~85% of the viewport. A percentage threshold was
// unusable here, because a full-width 16:9 clip can be taller than the
// viewport and never reach a high ratio.
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => setInView(entries[0].isIntersecting),
      { threshold: 0, rootMargin: '0px 0px -15% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
}

function CinematicClip({ clip, index }) {
  const [ref, inView] = useInView();
  const videoRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [userOptedIn, setUserOptedIn] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setReducedMotion(prefersReducedMotion());
  }, []);

  // Mirror the element's real play/pause state so the control label and icon
  // can never drift from what is actually happening.
  //
  // The 'play' guard is the important part: the element also carries the
  // browser's own autoplay attribute, and that can fire once data arrives -
  // including for a clip that has scrolled off screen. This immediately
  // re-pauses anything that is not actually in view, whichever path started
  // it. Chrome refused to autoplay via the observer alone; Edge did not.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    const sync = () => {
      const nowPlaying = !el.paused;
      setPlaying(nowPlaying);
      if (nowPlaying) setStarted(true);
    };
    // Decide visibility from real geometry rather than the observer's flag.
    // If IntersectionObserver is slow or never fires, the flag stays false and
    // this guard would instantly kill autoplay on every clip - which showed up
    // as black frames on one machine while working fine on another.
    const isActuallyVisible = () => {
      const box = el.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight * 0.9;
    };
    const enforceVisibility = () => {
      // Off screen always means paused. Reduced motion is deliberately NOT
      // checked here - it should stop the automatic start, never veto a play
      // the visitor explicitly asked for.
      if (!isActuallyVisible()) {
        if (!el.paused) el.pause();
      } else if (el.paused && !manualPause && (!reducedMotion || userOptedIn)) {
        const attempt = el.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }
    };

    el.addEventListener('play', sync);
    el.addEventListener('pause', sync);
    el.addEventListener('play', enforceVisibility);
    el.addEventListener('loadeddata', enforceVisibility);
    el.addEventListener('canplay', enforceVisibility);
    window.addEventListener('focus', enforceVisibility);
    document.addEventListener('visibilitychange', enforceVisibility);

    return () => {
      el.removeEventListener('play', sync);
      el.removeEventListener('pause', sync);
      el.removeEventListener('play', enforceVisibility);
      el.removeEventListener('loadeddata', enforceVisibility);
      el.removeEventListener('canplay', enforceVisibility);
      window.removeEventListener('focus', enforceVisibility);
      document.removeEventListener('visibilitychange', enforceVisibility);
    };
  }, [reducedMotion, manualPause, userOptedIn]);

  // Drive playback from visibility too. The element's own autoplay attribute
  // covers the common case; this observer-driven call is what makes a clip
  // start once it scrolls into view after arriving off screen.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Ask the element directly instead of trusting a loadeddata event, which
    // can fire before React attaches its handler and then never be seen again.
    const attemptPlay = () => {
      const box = el.getBoundingClientRect();
      const onScreen = box.bottom > 0 && box.top < window.innerHeight * 0.9;
      const shouldPlay = onScreen && !manualPause && (!reducedMotion || userOptedIn);
      if (shouldPlay) {
        if (el.paused) {
          const attempt = el.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
        }
      } else if (!el.paused) {
        el.pause();
      }
    };

    if (el.readyState >= 2) attemptPlay();

    // Catch cases where the media events arrived before this effect ran.
    const retry = setTimeout(attemptPlay, 600);
    const retry2 = setTimeout(attemptPlay, 2000);
    return () => {
      clearTimeout(retry);
      clearTimeout(retry2);
    };
  }, [inView, status, reducedMotion, manualPause, userOptedIn]);

  // Scrolling away resets a manual pause so the clip resumes on return.
  useEffect(() => {
    if (!inView) setManualPause(false);
  }, [inView]);

  return (
    <div
      className="cine-clip"
      style={{ ...styles.clip, animationDelay: `${0.1 + index * 0.1}s` }}
    >
      <div ref={ref} style={{ ...styles.frame, aspectRatio: clip.ratio || '16 / 9' }}>
        <video
          ref={videoRef}
          src={clip.src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedData={() => setStatus('ready')}
          onError={() => setStatus('error')}
          style={styles.video}
        />

        {status === 'error' ? (
          <div style={styles.missing}>
            <p style={styles.missingText}>Clip not found</p>
            <span style={styles.missingPath}>public{clip.src}</span>
          </div>
        ) : (
          <button
            type="button"
            className={`cine-toggle${started || playing ? '' : ' cine-toggle-always'}`}
            onClick={() => {
              setStarted(true);
              // Decide from the element's real state rather than toggling a
              // flag blindly - a clip paused because it was off screen was
              // never "manually paused", so toggling left it stuck.
              if (videoRef.current && videoRef.current.paused) {
                setUserOptedIn(true);
                setManualPause(false);
                // Play immediately rather than waiting for a state round-trip,
                // so a click always starts the clip even if the observer is
                // not reporting on this machine.
                const attempt = videoRef.current.play();
                if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
              } else {
                setManualPause(true);
              }
            }}
            aria-label={`${playing ? 'Pause' : 'Play'} clip ${index + 1}`}
            style={styles.toggle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(201,169,110,0.22)';
              e.currentTarget.style.borderColor = '#c9a96e';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(10,10,10,0.6)';
              e.currentTarget.style.borderColor = 'rgba(201,169,110,0.5)';
            }}
          >
            {playing ? '❙❙' : '▶'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Cinematics() {
  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.page}>
        <div className="cine-header" style={styles.header}>
          <span style={styles.label}>Atmos</span>
          <h1 style={styles.heading}>
            Cinematics<br />
          </h1>
        </div>

        {clips.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>Coming soon</p>
            <p style={styles.emptyHint}>
              Drop MP4s into public/videos/cinematics/ to populate this page
            </p>
          </div>
        ) : (
          clips.map((clip, i) => (
            <CinematicClip key={clip.src} clip={clip} index={i} />
          ))
        )}
      </div>
    </>
  );
}